/**
 * The realtime vocabulary, with no React and no EventSource in it.
 *
 * WHY A SEPARATE MODULE. The client, the provider, the hook and every feature that listens all
 * speak about the same frames. Putting the names next to the component that happens to consume
 * them first is how a second, slightly different spelling of `chat.message` appears — and a
 * subscriber whose event name is wrong by one character is silent, not broken, which is the
 * hardest kind of bug to see.
 *
 * WHAT A FRAME IS. A HINT, never the truth. Every payload carries ids and nothing else — the
 * server enforces it with a field catalogue since 18 September 2026 (§13) — and the stream is
 * best-effort. A screen therefore reacts to a frame by REFETCHING the query that owns the data,
 * never by splicing the payload into the cache as though it were a server answer.
 *
 * WHAT IS NEW SINCE 18 SEPTEMBER. Frames carry an `id` and a reconnect RESUMES from it, so a
 * missed frame is replayed rather than lost. `resync` is the server saying it cannot prove that:
 * refetch what is on screen. `ping` is the heartbeat, now a named event, which is what a
 * watchdog can see.
 */

/** Every event name the server emits on `GET /notifications/stream`. */
export const REALTIME_EVENTS = [
  /** First frame of a stream: who the server thinks you are, which customer, and its clock. */
  "ready",
  /**
   * The server could not resume where this client left off (first connect, an id it no longer
   * keeps, too long a backlog): refetch what is on screen.
   */
  "resync",
  /** The heartbeat, every `heartbeatSeconds`. A named event so silence is detectable. */
  "ping",
  "notification.created",
  /**
   * İstediği iş halledilen bildirimler kapandı (1 Kasım 2026): onay verildi, talep sonuçlandı, görev
   * başkasına geçti — kim yapmış olursa olsun. Yalnız sahibine, kendi satırlarının kimlikleri: zil ve
   * kutu yeniden okunur, kapanan satır listeden düşer.
   */
  "notification.settled",
  /**
   * Bu kişinin adı, iletişimi ya da giriş e-postası değişti (kisi-bilgi 19 Ekim 2026): oturumu
   * (Panelim'deki «Merhaba», başlıktaki ad) ve Profil'i yeniden oku. Yalnız kimlik taşır.
   */
  "person.changed",
  "chat.message",
  /** An author edited their own message; the body on screen is stale. */
  "chat.message.edited",
  /** An author (or a group owner) redacted one: the row stays, the body is gone. */
  "chat.message.redacted",
  "chat.read",
  "chat.typing",
  "chat.conversation",
  "converter.job",
  /**
   * Yüklenen bir dosyanın tarama durumu (K3): `CLEAN`, `INFECTED`, `SCAN_FAILED`. Yalnız yükleyene
   * gidiyor ve yalnız kimlik + durum taşıyor — ret GEREKÇESİ çerçevede yok, gerekirse
   * `GET /files/{id}/status` ile okunuyor.
   */
  "file.status",
  /** This session ended — the server closes the stream right after sending it. */
  "session.revoked",
  /** A token refresh moved the stream to a successor session; nothing for a client to do. */
  "session.rotated",
  /** The session switched customer: forget the event id and reconnect into the new one. */
  "session.tenant_changed",
  /** Too many streams: the oldest was closed so the newest could open. */
  "overflow",
  /** The session's own expiry passed. */
  "expired",
  /** The API is shutting down; reconnect after the deploy. */
  "shutdown",
] as const;

export type RealtimeEventName = (typeof REALTIME_EVENTS)[number];

/** The parsed frame handed to a subscriber. `data` is whatever the server put in it. */
export interface RealtimeFrame<T = Record<string, unknown>> {
  event: RealtimeEventName;
  data: T;
}

export interface ReadyPayload {
  userId: string;
  /** The customer this stream is bound to; a switch closes it (`session.tenant_changed`). */
  tenantId: string;
  serverTime: string;
  heartbeatSeconds: number;
}

export interface ChatMessagePayload {
  conversationId: string;
  messageId: string;
  senderUserId: string;
}

/** Both edit and redaction carry only the pair; the message itself is refetched. */
export interface ChatMessageChangedPayload {
  conversationId: string;
  messageId: string;
}

export interface ChatReadPayload {
  conversationId: string;
  userId: string;
  messageId: string;
}

export interface ChatTypingPayload {
  conversationId: string;
  userId: string;
}

export interface ChatConversationPayload {
  conversationId: string;
  action: string;
  userId?: string;
  userIds?: string[];
}

export interface ConverterJobPayload {
  jobId: string;
  status: string;
}

export interface FileStatusPayload {
  fileId: string;
  status: string;
}

export interface NotificationCreatedPayload {
  notificationId: string;
}

export interface NotificationSettledPayload {
  /** Kapanan satırlar — en çok 50; çerçeve yalnız ipucu, kutu yeniden okunuyor. */
  notificationIds?: string[];
}

/**
 * Whether a name is one the server actually publishes.
 *
 * An unknown event is dropped rather than dispatched: a typo in a subscriber would otherwise
 * wait forever for a frame that never comes, and a new server event nobody added here would
 * arrive as `undefined` at every listener.
 */
export function isRealtimeEvent(name: string): name is RealtimeEventName {
  return (REALTIME_EVENTS as readonly string[]).includes(name);
}

/** The frames the server sends immediately before closing the socket on purpose. */
export const TERMINAL_EVENTS: readonly RealtimeEventName[] = ["session.revoked", "expired", "overflow"];
